import jsPDF from "jspdf";
import dayjs from "dayjs";

const PAGE_WIDTH = 215.9;
const PAGE_HEIGHT = 279.4;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const COLS = [
  { key: "timeLabel", label: "Time", width: 42 },
  { key: "client", label: "Client", width: 48 },
  { key: "service", label: "Service", width: 58 },
  { key: "status", label: "Status", width: CONTENT_WIDTH - 42 - 48 - 58 },
];

function drawFooter(pdf, page, pages) {
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(110);
  const printed = dayjs().format("MMM D, YYYY h:mm A");
  pdf.text(`Printed ${printed}`, MARGIN, PAGE_HEIGHT - 8);
  pdf.text(`Page ${page} of ${pages}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 8, {
    align: "right",
  });
}

function lineCount(pdf, text, width) {
  const lines = pdf.splitTextToSize(text || " ", width - 2);
  return { lines, height: Math.max(lines.length, 1) * 4.2 };
}

export function renderStylistDaySheetPdf(sheet) {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "letter",
  });

  const pages = [];
  let y = 0;

  const newPage = () => {
    if (pages.length) pdf.addPage();
    pages.push(true);
    pdf.setFillColor(17, 17, 17);
    pdf.rect(0, 0, PAGE_WIDTH, 22, "F");
    pdf.setFillColor(197, 160, 111);
    pdf.rect(0, 22, PAGE_WIDTH, 1.4, "F");
    pdf.setTextColor(255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text(sheet.locationName || "Day sheet", MARGIN, 10);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text(sheet.dateLabel, PAGE_WIDTH - MARGIN, 10, { align: "right" });
    if (sheet.salonAddress) {
      pdf.setFontSize(8);
      pdf.setTextColor(220);
      pdf.text(sheet.salonAddress, MARGIN, 16);
    }
    y = 30;
  };

  const ensureRoom = (height) => {
    if (y + height > PAGE_HEIGHT - 16) newPage();
  };

  newPage();

  pdf.setTextColor(17);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text(sheet.stylistName || "Stylist", MARGIN, y);
  y += 6;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(70);
  const meta = [
    sheet.shiftLabel ? `Shift ${sheet.shiftLabel}` : "",
    `${sheet.appointmentCount} appointment${sheet.appointmentCount === 1 ? "" : "s"}`,
    `${sheet.bookedLabel} booked`,
    sheet.openLabel ? `${sheet.openLabel} open` : "",
  ]
    .filter(Boolean)
    .join("   ·   ");
  pdf.text(meta, MARGIN, y);
  y += 6;

  const drawHeader = () => {
    ensureRoom(10);
    pdf.setFillColor(244, 241, 236);
    pdf.rect(MARGIN, y, CONTENT_WIDTH, 7, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(80);
    let x = MARGIN;
    COLS.forEach((col) => {
      pdf.text(col.label.toUpperCase(), x + 1.5, y + 4.6);
      x += col.width;
    });
    y += 7;
  };

  drawHeader();

  if (!sheet.rows.length) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(10);
    pdf.setTextColor(90);
    pdf.text("No appointments on this day.", MARGIN, y + 8);
  }

  sheet.rows.forEach((row) => {
    pdf.setFont("helvetica", row.kind === "open" ? "italic" : "normal");
    pdf.setFontSize(9);
    const measured = COLS.map((col) =>
      lineCount(pdf, row[col.key] || (col.key === "service" && row.kind === "open" ? "—" : ""), col.width)
    );
    const rowHeight = Math.max(...measured.map((item) => item.height)) + 3.2;
    if (y + rowHeight > PAGE_HEIGHT - 16) {
      newPage();
      drawHeader();
    }
    if (row.kind === "open") {
      pdf.setFillColor(248, 248, 248);
      pdf.rect(MARGIN, y, CONTENT_WIDTH, rowHeight, "F");
      pdf.setTextColor(120);
    } else {
      pdf.setTextColor(20);
    }
    let x = MARGIN;
    measured.forEach((item, index) => {
      pdf.text(item.lines, x + 1.5, y + 4.4);
      x += COLS[index].width;
    });
    y += rowHeight;
    pdf.setDrawColor(225);
    pdf.setLineWidth(0.15);
    pdf.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  });

  const total = pdf.getNumberOfPages();
  for (let page = 1; page <= total; page += 1) {
    pdf.setPage(page);
    drawFooter(pdf, page, total);
  }

  return pdf;
}

export function printPdfBlob(blob) {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Stylist day sheet");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  };
  return url;
}
